/**
 * NextStepsFeed — "what should I do next?", answered before being asked.
 *
 * Rows are DETERMINISTIC: computed by the page from the deadline engine and
 * setup state (never LLM-authored — same guardrail as the chat: figures come
 * from the engine, the AI only helps act on them). Each row offers the direct
 * action and an "Ask Vee" hand-off with a pre-scoped prompt.
 */

import { FiChevronRight } from 'react-icons/fi';
import SectionCard from './SectionCard';
import { Icon } from '../common/Icon';

const TONES = {
  crit: { chip: '#9a564a', bg: 'rgba(154,86,74,0.05)', border: 'rgba(154,86,74,0.25)' },
  warn: { chip: '#8a6a1f', bg: 'rgba(184,151,58,0.06)', border: 'rgba(184,151,58,0.3)' },
  info: { chip: '#3a4a66', bg: 'rgba(58,74,102,0.05)', border: 'rgba(58,74,102,0.22)' },
};

export default function NextStepsFeed({ steps, onAskVee }) {
  return (
    <SectionCard icon="quick-actions" iconTone="#145f5f" title="Next Steps" subtitle="Computed from your filings — not AI guesses">
      <div className="p-3 flex flex-col gap-2">
        {steps.length === 0 ? (
          <div className="flex items-center gap-2.5 px-3 py-3 rounded-[12px]" style={{ background: 'rgba(74,107,63,0.06)' }}>
            <Icon name="success" size={16} style={{ color: '#4a6b3f' }} />
            <div className="text-[12px] text-[#4a6b3f] font-semibold">All clear — nothing needs your attention right now.</div>
          </div>
        ) : steps.map((step) => {
          const tone = TONES[step.tone] || TONES.info;
          return (
            <div
              key={step.id}
              className="px-3 py-2.5 rounded-[12px] border"
              style={{ background: tone.bg, borderColor: tone.border }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold text-[#08111f] truncate">{step.title}</div>
                  <div className="text-[10.5px] text-[#7a7060] mt-[1px]">{step.why}</div>
                </div>
                {step.badge && (
                  <span className="shrink-0 text-[10px] font-bold whitespace-nowrap" style={{ color: tone.chip }}>{step.badge}</span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <button
                  type="button"
                  onClick={step.onDo}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 h-7 rounded-lg text-white transition-all active:scale-95"
                  style={{ background: '#145f5f' }}
                >
                  {step.doLabel} <FiChevronRight size={11} />
                </button>
                <button
                  type="button"
                  onClick={() => onAskVee(step.askPrompt)}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold pl-1.5 pr-2.5 h-7 rounded-lg border transition-colors hover:border-[#145f5f] hover:bg-white text-[#145f5f]"
                  style={{ borderColor: 'rgba(20,95,95,0.3)', background: 'transparent' }}
                >
                  <img src="/vee-mascot.svg" alt="" className="w-4 h-4 rounded-[5px]" /> Ask Vee
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}
